import { forwardRef, type ButtonHTMLAttributes } from "react"
import { Loader2 } from "lucide-react"
import { cn } from "../../lib/cn"

export type ButtonVariant = "primary" | "secondary" | "destructive" | "ghost"
export type ButtonSize = "sm" | "md" | "lg"

const variantClasses: Record<ButtonVariant, string> = {
  primary:
    "bg-accent text-text-on-accent hover:opacity-90 focus-visible:ring-accent/40 disabled:opacity-50",
  secondary:
    "border border-border-strong bg-surface text-text-primary hover:bg-surface-muted focus-visible:ring-accent/40 disabled:opacity-50",
  destructive:
    "bg-danger text-white hover:opacity-90 focus-visible:ring-danger/40 disabled:opacity-50",
  ghost:
    "text-text-secondary hover:bg-surface-muted hover:text-text-primary focus-visible:ring-accent/40 disabled:opacity-50",
}

const sizeClasses: Record<ButtonSize, string> = {
  sm: "h-8 px-sm text-caption gap-xs",
  md: "h-10 px-lg text-body-sm gap-xs",
  lg: "h-12 px-xl text-body gap-sm",
}

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant
  size?: ButtonSize
  loading?: boolean
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = "primary", size = "md", loading = false, disabled, className, children, ...props },
  ref,
) {
  return (
    <button
      ref={ref}
      disabled={disabled || loading}
      className={cn(
        "inline-flex items-center justify-center rounded-button font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2",
        variantClasses[variant],
        sizeClasses[size],
        className,
      )}
      {...props}
    >
      {loading && <Loader2 className="size-4 animate-spin" aria-hidden="true" />}
      {children}
    </button>
  )
})
